/*
  And Then Some: the Shop By Category department toggle.

  It does one job: decide which department's tiles are visible, remember the
  choice, and point View All at the matching collection. Everything else about
  the rail, the arrows and the scroll indicator, belongs to assets/ats-rail.js
  and is not duplicated here.

  SCOPED PER SECTION. Every lookup starts from the <ats-rail> host rather than
  the document, so two instances of the section on one page cannot fight over
  each other's tabs. The host also carries the two collection URLs as data
  attributes, because Liquid knows them and this file must not hardcode a path.

  THE PAGE WORKS WITHOUT THIS FILE. Every tile of both departments is in the
  markup and visible; the toggle simply does nothing. That is the right no-script
  state: all the category links stay reachable, which is also why both
  departments are rendered rather than one being built on demand.

  ⚠️ REDUCED MOTION SKIPS THE DELAY, NOT JUST THE FADE. The CSS already
  neutralises the .swapping opacity, but leaving the 180ms timer in place would
  make the swap feel laggy for no visual payoff, so the swap happens at once.

  ⚠️ AFTER A SWAP THE RAIL IS RESET AND A scroll EVENT IS DISPATCHED. ats-rail.js
  recalculates its arrows and indicator from a scroll event, and the new set of
  tiles is a different width, so without this the arrows keep the previous
  department's enabled state and the indicator keeps its old thumb.
*/
(function () {
  'use strict';

  var KEY = 'ats_department';
  var VALID = { Women: true, Men: true };
  var FADE_MS = 180;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Only ever returns 'Women' or 'Men'. A missing key, a corrupt value, or a
     browser that refuses localStorage all land on Women, per the companion. */
  function remembered() {
    try {
      var v = localStorage.getItem(KEY);
      return VALID[v] ? v : 'Women';
    } catch (e) {
      return 'Women';
    }
  }

  function persist(dept) {
    try {
      localStorage.setItem(KEY, dept);
    } catch (e) {
      /* Private mode, or storage full. The toggle still works for this visit. */
    }
  }

  function init(host) {
    if (!host || host.dataset.catsReady === 'true') return;
    host.dataset.catsReady = 'true';

    var scroller = host.querySelector('[data-rail-scroller]');
    var tiles = host.querySelectorAll('.hp-cat[data-dept]');
    var tabs = host.querySelectorAll('.hp-tab[data-dept]');
    var viewAll = host.querySelector('[data-cats-viewall]');
    if (!scroller || !tiles.length) return;

    var urls = {
      Women: host.dataset.womensUrl || '',
      Men: host.dataset.mensUrl || '',
    };

    /* Which departments actually have tiles. Counted from the DOM rather than
       trusted from the data attributes, so the two cannot disagree. */
    var available = { Women: 0, Men: 0 };
    for (var i = 0; i < tiles.length; i++) {
      var d = tiles[i].getAttribute('data-dept');
      if (available[d] !== undefined) available[d]++;
    }

    /* A remembered department with nothing in it falls back to the one that has
       tiles, rather than showing an empty rail. */
    function resolve(dept) {
      if (available[dept] > 0) return dept;
      return available.Women > 0 ? 'Women' : 'Men';
    }

    function apply(dept) {
      for (var i = 0; i < tiles.length; i++) {
        tiles[i].hidden = tiles[i].getAttribute('data-dept') !== dept;
      }

      for (var j = 0; j < tabs.length; j++) {
        var on = tabs[j].getAttribute('data-dept') === dept;
        tabs[j].setAttribute('aria-selected', on ? 'true' : 'false');
        /* The class mirrors the attribute. The stylesheet matches on both, so
           they cannot produce different results if one is ever set alone. */
        tabs[j].classList.toggle('on', on);
      }

      if (viewAll && urls[dept]) viewAll.setAttribute('href', urls[dept]);

      /* The tile set changed width, so ats-rail.js has to remeasure. */
      scroller.scrollLeft = 0;
      scroller.dispatchEvent(new Event('scroll'));
    }

    function paint(dept, animate) {
      var target = resolve(dept);
      persist(target);

      if (!animate || reduceMotion.matches) {
        apply(target);
        return;
      }

      scroller.classList.add('swapping');
      window.setTimeout(function () {
        apply(target);
        scroller.classList.remove('swapping');
      }, FADE_MS);
    }

    for (var k = 0; k < tabs.length; k++) {
      tabs[k].addEventListener('click', function (event) {
        var next = event.currentTarget.getAttribute('data-dept');
        if (next === current()) return;
        paint(next, true);
      });
    }

    /* Read back from the DOM rather than holding a variable, so the state
       cannot drift from what is on screen. */
    function current() {
      for (var i = 0; i < tabs.length; i++) {
        if (tabs[i].getAttribute('aria-selected') === 'true') {
          return tabs[i].getAttribute('data-dept');
        }
      }
      return null;
    }

    paint(remembered(), false);
  }

  function boot() {
    var hosts = document.querySelectorAll('[data-ats-cats]');
    for (var i = 0; i < hosts.length; i++) init(hosts[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
